/* eslint-disable arrow-body-style */
import '../_dialer.scss';
import React, { memo } from 'react';
import { ImageIcon } from '@saleshandy/design-system';
import { Cross } from '@saleshandy/icons';
import { VerticalDialerDots } from '../../../svg/verticalDialerDots';
import { Images } from '../../../app-constants';
import { formatCallDuration } from '../utils/helper';
import { CallManagementState } from '../types';
import NetworkStrengthBars, {
  NetworkLevel,
} from '../../../svg/network-strength-bars';
import {
  OverlayTooltip,
  Placement,
  TriggerType,
} from '../../../design-system/components/overlay';

interface DialerHeaderProps {
  title: string;
  onClose?: () => void;
  showNetworkIcon?: boolean;
  numberOfActiveNetworks?: number;
  isCallConnected?: boolean;
  callStatusLabel?: string | null;
  /** Call duration in seconds; shown in header when call is connected */
  callDuration?: number;
  callManagementState?: CallManagementState;
  networkLevel?: NetworkLevel;
  onPreCallTestClick?: () => void;
  isInPip?: boolean;
  pipContainer?: HTMLElement | null;
}

const DialerHeader: React.FC<DialerHeaderProps> = ({
  title,
  onClose,
  showNetworkIcon = false,
  numberOfActiveNetworks = 4,
  isCallConnected = false,
  callDuration = 0,
  callManagementState,
  networkLevel = 4,
  onPreCallTestClick,
  isInPip = false,
  pipContainer = null,
}) => {
  return (
    <div className="dialer-header">
      <div className="dialer-header-left">
        {!isInPip && (
          <span className="dialer-header-dots-container">
            <VerticalDialerDots
              width={14}
              height={14}
              className="dialer-header-dots-icon"
            />
          </span>
        )}
        <div
          className={`dialer-header-title-container ${
            onClose ? '' : 'connected'
          }`}
        >
          {(callManagementState?.callStatus === 'ringing' ||
            callManagementState?.callStatus === 'answered') && (
            <span className="dialer-header-network-bars">
              <NetworkStrengthBars level={networkLevel} size={16} />
            </span>
          )}
          <p
            className={`dialer-header-title ${
              title === 'Ringing...' ? 'ringing' : ''
            } ${title === 'Connected' ? 'call-connected' : ''}`}
          >
            {title}
          </p>
        </div>
      </div>

      <div className="dialer-header-right">
        {isCallConnected &&
        callManagementState?.callTwilioStatus === 'connected' &&
        callManagementState?.callStatus === 'answered' ? (
          <span className="dialer-header-call-duration">
            {formatCallDuration(callDuration)}
          </span>
        ) : (
          <>
            {!isCallConnected &&
              (onClose || isInPip) &&
              (isInPip ? (
                <div className="dialer-headset-wrapper">
                  <button
                    type="button"
                    className="dialer-headset"
                    onClick={onPreCallTestClick}
                    aria-label="Headset"
                  >
                    <ImageIcon
                      src={Images.Headset}
                      alt="headset"
                      width={16}
                      height={16}
                      className="dialer-headset-icon"
                    />
                  </button>
                  <span className="dialer-headset-tooltip">
                    Test your microphone, speaker and network strength.
                  </span>
                </div>
              ) : (
                <OverlayTooltip
                  text="Test your microphone, speaker and network strength."
                  placement={Placement.Bottom}
                  className="dialer-header-tooltip"
                  trigger={[TriggerType.Hover, TriggerType.Focus]}
                >
                  <button
                    type="button"
                    className="dialer-headset"
                    onClick={onPreCallTestClick}
                    aria-label="Headset"
                  >
                    <ImageIcon
                      src={Images.Headset}
                      alt="headset"
                      width={16}
                      height={16}
                      className="dialer-headset-icon"
                    />
                  </button>
                </OverlayTooltip>
              ))}
            {onClose && !isCallConnected && (
              <button
                type="button"
                className="dialer-header-close-btn"
                onClick={onClose}
                aria-label="Close dialer"
              >
                <Cross width={16} height={16} />
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default memo(DialerHeader);
